/**
 * Care Manager Data Service
 * Carga datos de Care Management desde archivos JSON estáticos por location.
 * Usa dynamic import para cargar solo los datos de la location seleccionada.
 *
 * Los JSONs se encuentran en: src/pages/Main/Temp/DataDashCareManager/{locationKey}/
 * Cada location tiene 6 archivos: overview.json, transitions.json, unmanaged_risk.json,
 * er_utilization.json, outreach.json, polypharmacy.json
 *
 * Migración futura: Reemplazar imports estáticos por llamadas API.
 */

// Cache de locations (se carga una sola vez)
let locationsCache = null;

/**
 * Obtiene la lista de locations disponibles para Care Manager.
 * Normaliza los campos para compatibilidad con LocationSelector (address, patients).
 *
 * Ejemplo de respuesta (normalizada):
 * [
 *   {
 *     "key": "fort_myers",
 *     "label": "Fort Myers",
 *     "address": "3900 BROADWAY STE C UNIT 13, Fort Myers, FL - 33901, Lee",
 *     "patients": 2013,
 *     "pcps": [...]
 *   }
 * ]
 *
 * @returns {Promise<Array>} Lista de locations
 */
export async function getLocations() {
  if (locationsCache) return locationsCache;
  const module = await import(
    "../../../pages/Main/Temp/DataDashCareManager/locations.json"
  );
  const raw = module.default?.locations || module.locations || [];
  // Normalizar campos para compatibilidad con LocationSelector
  locationsCache = raw.map((loc) => ({
    ...loc,
    address: loc.fullAddress || loc.address || "",
    patients: loc.totalPatients ?? loc.patients ?? 0,
  }));
  return locationsCache;
}

/**
 * Obtiene el overview (KPIs) de una location.
 *
 * @param {string} locationKey - Key de la location (e.g., "fort_myers")
 *
 * Ejemplo de respuesta:
 * {
 *   "kpis": { "inActiveCM": 15, "unmanagedHighRisk": 221, "postDischargePending": 27, "erFrequentFlyers": 49 },
 *   "cmStatusBreakdown": { "referred": 12, "engaged": 1, "attempted": 2 },
 *   "totalPatients": 2013,
 *   "healthStatusDistribution": { "Well": 928, "Acute": 438, ... }
 * }
 *
 * @returns {Promise<Object>} Overview data
 */
export async function getOverview(locationKey) {
  const module = await import(
    `../../../pages/Main/Temp/DataDashCareManager/${locationKey}/overview.json`
  );
  return module.default || module;
}

/**
 * Obtiene datos de Transition of Care (discharges) de una location.
 *
 * @param {string} locationKey - Key de la location
 *
 * Ejemplo de respuesta:
 * {
 *   "stats": { "totalDischarges": 27, "avgLaceScore": 13.3, "highLaceCount": 7, "expiredCount": 2 },
 *   "discharges": [{ "cipId": ..., "name": "...", "facility": "...", "laceScore": 19, ... }]
 * }
 *
 * @returns {Promise<Object>} Transitions data
 */
export async function getTransitions(locationKey) {
  const module = await import(
    `../../../pages/Main/Temp/DataDashCareManager/${locationKey}/transitions.json`
  );
  return module.default || module;
}

/**
 * Obtiene datos de Unmanaged High Risk de una location.
 *
 * @param {string} locationKey - Key de la location
 *
 * Ejemplo de respuesta:
 * {
 *   "summary": { "atRiskNoCM": 184, "complexNoCM": 28, "eolNoCM": 9, "total": 221 },
 *   "patients": [{ "cipId": ..., "name": "...", "healthStatus": "End of Life", "globalIndicator": 7, ... }]
 * }
 *
 * @returns {Promise<Object>} Unmanaged risk data
 */
export async function getUnmanagedRisk(locationKey) {
  const module = await import(
    `../../../pages/Main/Temp/DataDashCareManager/${locationKey}/unmanaged_risk.json`
  );
  return module.default || module;
}

/**
 * Obtiene datos de ER & Utilization de una location.
 *
 * @param {string} locationKey - Key de la location
 *
 * Ejemplo de respuesta:
 * {
 *   "stats": { "frequentFlyers": 49, "totalPreventableER": 154, "bhRelatedER": 0 },
 *   "topFlyers": [{ "cipId": ..., "name": "...", "erEvents": 8, "ppvER": "MEDIUM", ... }]
 * }
 *
 * @returns {Promise<Object>} ER utilization data
 */
export async function getERUtilization(locationKey) {
  const module = await import(
    `../../../pages/Main/Temp/DataDashCareManager/${locationKey}/er_utilization.json`
  );
  return module.default || module;
}

/**
 * Obtiene datos de Outreach de una location.
 *
 * @param {string} locationKey - Key de la location
 *
 * Ejemplo de respuesta:
 * {
 *   "stats": { "patientsToContact": 1298, "withPhone": 1046, "withEmail": 845 },
 *   "patients": [{ "cipId": ..., "name": "...", "phone": "...", "email": "...", ... }]
 * }
 *
 * @returns {Promise<Object>} Outreach data
 */
export async function getOutreach(locationKey) {
  const module = await import(
    `../../../pages/Main/Temp/DataDashCareManager/${locationKey}/outreach.json`
  );
  return module.default || module;
}

/**
 * Obtiene datos de Polypharmacy de una location.
 *
 * @param {string} locationKey - Key de la location
 *
 * Ejemplo de respuesta:
 * {
 *   "stats": { "totalWith5Plus": 88, "totalWith8Plus": 15, "withoutRecentLabs": 65 },
 *   "patients": [{ "cipId": ..., "name": "...", "medications": 15, "recentLabs": "N", ... }]
 * }
 *
 * @returns {Promise<Object>} Polypharmacy data
 */
export async function getPolypharmacy(locationKey) {
  const module = await import(
    `../../../pages/Main/Temp/DataDashCareManager/${locationKey}/polypharmacy.json`
  );
  return module.default || module;
}

/**
 * Carga todos los 6 datasets de una location en paralelo.
 *
 * @param {string} locationKey - Key de la location (e.g., "fort_myers")
 *
 * @returns {Promise<Object>} Objeto con los 6 datasets
 */
export async function getLocationData(locationKey) {
  const [overview, transitions, unmanagedRisk, erUtilization, outreach, polypharmacy] =
    await Promise.all([
      getOverview(locationKey),
      getTransitions(locationKey),
      getUnmanagedRisk(locationKey),
      getERUtilization(locationKey),
      getOutreach(locationKey),
      getPolypharmacy(locationKey),
    ]);

  return { overview, transitions, unmanagedRisk, erUtilization, outreach, polypharmacy };
}
